export type Auth0CacheLocation = 'memory' | 'localstorage';

const SUPPORTED_CACHE_LOCATIONS: ReadonlySet<string> =
  new Set<Auth0CacheLocation>(['memory', 'localstorage']);

export interface Auth0RenewalOptions {
  useRefreshTokens: boolean | undefined;
  cacheLocation: Auth0CacheLocation | undefined;
}

const LOCALHOST_HOSTNAMES = new Set(['localhost', '127.0.0.1', '[::1]', '::1']);

/**
 * Auth0 never skips the consent screen for a `localhost` origin, so iframe
 * silent token renewal (`prompt=none`) always throws `consent_required` and
 * strands the app after login — every guarded API call (e.g. the
 * SecurityService permission load) then fails. On localhost we switch to
 * refresh tokens + a localStorage cache: renewal uses the `refresh_token`
 * grant instead of the iframe, sidestepping consent entirely and surviving
 * page reloads.
 *
 * Deployed origins are env-driven: `useRefreshTokens` and `cacheLocation` come
 * from the app's environment, and an app that sets neither keeps the SDK
 * defaults (iframe renewal, in-memory cache). An app that opts into refresh
 * tokens on a deployed origin must also opt into the localStorage cache (Bug
 * 34695): with the in-memory cache a reload drops the refresh token, so
 * renewal falls back to the third-party `/authorize` iframe, which Safari's ITP
 * blocks, and every call waits ~10s and then 401s. An unsupported env value is
 * dropped rather than handed to the SDK.
 */
export function resolveAuth0RenewalOptions(
  hostname: string | undefined,
  envUseRefreshTokens: boolean | undefined,
  envCacheLocation?: Auth0CacheLocation
): Auth0RenewalOptions {
  if (hostname !== undefined && LOCALHOST_HOSTNAMES.has(hostname)) {
    return { useRefreshTokens: true, cacheLocation: 'localstorage' };
  }
  const cacheLocation =
    envCacheLocation !== undefined &&
    SUPPORTED_CACHE_LOCATIONS.has(envCacheLocation)
      ? envCacheLocation
      : undefined;
  return { useRefreshTokens: envUseRefreshTokens, cacheLocation };
}
